import { ReactNode, useCallback, useMemo } from 'react'

import {
  type BookingStatusDialogItem,
  type KyrgyzstanBookingStatus,
  type KyrgyzstanBookingStatusInfo,
  bookingStatusConfig,
  bookingStatusDialogItems,
} from 'entities/kyrgyzstan/model'

import type { PaymentStatus } from 'shared/api/kyrgyzstan/orders'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { getRawColor } from 'shared/lib/getRawColor'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

interface UseBookingStatusDialogParams {
  status: KyrgyzstanBookingStatus
  statusInfo?: KyrgyzstanBookingStatusInfo
  paymentStatus?: PaymentStatus
}

interface UseBookingStatusDialogResult {
  statusTranslationKey: string
  statusTheme: 'pending' | 'success' | 'danger'
  dialogItems: BookingStatusDialogItem[]
  dateOptions: { arrive_date: string; out_date: string }
  openStatusDialog: (content: ReactNode) => void
}

export const useBookingStatusDialog = ({
  status,
  statusInfo,
  paymentStatus,
}: UseBookingStatusDialogParams): UseBookingStatusDialogResult => {
  const { openDialog } = useDialogBottomSheet()

  const statusConfig = useMemo(() => {
    const config = bookingStatusConfig[status]
    return (
      config || {
        translationKey: 'kyrgyzstan_block.booking_card.status.pending',
        theme: 'pending' as const,
      }
    )
  }, [status])

  const dialogItems = useMemo(() => {
    const statusItems = bookingStatusDialogItems[status]

    if (!statusItems || !Array.isArray(statusItems)) {
      return []
    }

    const items = [...statusItems]

    if (status === 'cancelled' && paymentStatus) {
      const refundItemIndex = items.findIndex(
        (item) =>
          item.translationKey ===
          'kyrgyzstan_block.booking_card.status_popup.cancelled.refund',
      )

      if (refundItemIndex !== -1) {
        items[refundItemIndex] = {
          ...items[refundItemIndex],
          translationKey:
            paymentStatus === 'prepayment'
              ? 'kyrgyzstan_block.booking_card.status_popup.cancelled.refund_prepayment'
              : 'kyrgyzstan_block.booking_card.status_popup.cancelled.refund',
        }
      }
    }

    return items
  }, [status, paymentStatus])

  const dateOptions = useMemo(
    () => ({
      arrive_date: statusInfo?.arriveDate
        ? dateFormat(statusInfo.arriveDate, DATE_FORMAT_LIST.FULL_DATE)
        : '—',
      out_date: statusInfo?.outDate
        ? dateFormat(statusInfo.outDate, DATE_FORMAT_LIST.FULL_DATE)
        : '—',
    }),
    [statusInfo?.arriveDate, statusInfo?.outDate],
  )

  const openStatusDialog = useCallback(
    (content: ReactNode) => {
      if (dialogItems.length === 0) {
        return
      }

      openDialog({
        backgroundColor: getRawColor('--white'),
        child: content,
      })
    },
    [dialogItems.length, openDialog],
  )

  return {
    statusTranslationKey: statusConfig.translationKey,
    statusTheme: statusConfig.theme,
    dialogItems,
    dateOptions,
    openStatusDialog,
  }
}
